import useCurrentOrgQuery from "./useCurrentOrgQuery";
import { useUserStateContext } from "@/shared/components/UserStateProvider";
import { useAddUserToDepartmentMutation } from "@/features/department/hooks/useAddUserToDepartmentMutation";
import { useRemoveUserFromDepartmentMutation } from "@/features/department/hooks/useRemoveUserFromDepartmentMutation";
import { useAddDepartmentToOrgMutation } from "@/features/department/hooks/useAddDepartmentToOrgMutation";
import { useRemoveDepartmentFromOrgMutation } from "@/features/department/hooks/useRemoveDepartmentFromOrgMutation";
import useEditDepartmentMutation from "@/features/department/hooks/useEditDepartmentMutation";
import useUpdateOrganizationInstanceSchedulingMutation from "@/features/org/hooks/useUpdateOrganizationInstanceSchedulingMutation";
import useApplyOrganizationInstanceSchedulingMutation from "@/features/org/hooks/useApplyOrganizationInstanceSchedulingMutation";

const useCurrentOrgState = () => {
    // Primary source: user context from /user endpoint (always available, filtered)
    const { currentOrg: contextOrg } = useUserStateContext();

    // Secondary source: detailed org data from /org (lazy-loaded for management features)
    const { status, data: currentOrgQueryData, refetch, isLoading } = useCurrentOrgQuery();

    // Use context org ID as the source of truth
    const orgId = contextOrg?.id;

    const removeUserFromDepartmentMutation = useRemoveUserFromDepartmentMutation();
    const addUserToDepartmentMutation = useAddUserToDepartmentMutation();
    const removeDepartmentFromOrgMutation = useRemoveDepartmentFromOrgMutation();
    const addDepartmentToOrgMutation = useAddDepartmentToOrgMutation();
    const editDepartmentMutation = useEditDepartmentMutation();
    const updateSchedulingMutation = useUpdateOrganizationInstanceSchedulingMutation();
    const applyScheduleMutation = useApplyOrganizationInstanceSchedulingMutation();

    const addUserToDepartment = async (departmentId: number, userId: number) => {
        addUserToDepartmentMutation.mutate({ departmentId, userId });
    }

    const removeUserFromDepartment = async (departmentId: number, userId: number) => {
        removeUserFromDepartmentMutation.mutate({ departmentId, userId });
    }

    const addDepartmentToOrg = async (name: string) => {
        if (!orgId) return;
        addDepartmentToOrgMutation.mutate({ name, orgId });
    }

    const removeDepartmentFromOrg = async (departmentId: number) => {
        if (!orgId) return;
        removeDepartmentFromOrgMutation.mutate({ departmentId, orgId });
    }

    const editDepartment = async (departmentId: number, name: string) => {
        if (!orgId) return;
        editDepartmentMutation.mutate({ departmentId, name, orgId });
    }

    const updateScheduling = async (scheduling: any) => {
        if (!orgId) return;
        scheduling.forEach((s: any) => {
            s.orgId = orgId;
        });
        updateSchedulingMutation.mutate(scheduling);
    }

    const applyScheduling = async () => {
        applyScheduleMutation.mutate();
    }

    return {
        isLoading,
        refetch,
        // Basic org info from /user context (filtered, always fresh)
        org: contextOrg,
        // Detailed data from /org endpoint (for management features)
        departments: currentOrgQueryData?.data?.departments,
        groups: currentOrgQueryData?.data?.groups,
        status,
        scheduling: currentOrgQueryData?.data?.instanceplannings || [],
        actions: {
            addUserToDepartment,
            removeUserFromDepartment,
            addDepartmentToOrg,
            removeDepartmentFromOrg,
            editDepartment,
            updateScheduling,
            applyScheduling
        }
    }
}

export { useCurrentOrgState };
export default useCurrentOrgState;
